import {
  defineComponent, reactive, watch, computed, PropType,
} from 'vue'
import { IconProps, IconState } from './interface'
import {
  Size, IconColor, IconTheme, IconStrokeLinecap, IconStrokeLinejoin,
} from '../../enums'

export default defineComponent({
  props: {
    icon: {
      type: Function as PropType<(p: IconProps) => string>,
      default: undefined,
    },
    size: {
      type: [String, Number] as PropType<Size | string | number>,
      default: 'default',
    },
    color: {
      type: String as PropType<IconColor>,
      default: undefined,
    },
    strokeWidth: {
      type: Number,
      default: 4,
    },
    strokeLinecap: {
      type: String as PropType<IconStrokeLinecap>,
      default: 'round',
    },
    strokeLinejoin: {
      type: String as PropType<IconStrokeLinejoin>,
      default: 'round',
    },
    theme: {
      type: String as PropType<IconTheme>,
      default: 'outline',
    },
    fill: {
      type: [String, Array] as (PropType<string> | PropType<string[]>),
      default: undefined,
    },
    rotate: {
      type: Number,
      default: 0,
    },
    spin: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    click(e: MouseEvent) {
      return e !== undefined
    },
    mousedown(e: MouseEvent) {
      return e !== undefined
    },
    mouseenter(e: MouseEvent) {
      return e !== undefined
    },
    mouseleave(e: MouseEvent) {
      return e !== undefined
    },
    mouseup(e: MouseEvent) {
      return e !== undefined
    },
    'state-change'(state: IconState) {
      return state !== undefined
    },
  },
  setup(props, { slots, emit }) {
    const state = reactive({
      pressing: false,
      hover: false,
    })

    watch(
      state,
      v => emit('state-change', v),
    )

    const presetSize = computed(
      () => typeof props.size === 'string' && ['small', 'default', 'large', 'extra-large'].includes(props.size),
    )

    const customSize = computed(
      () => !presetSize.value && (
        typeof props.size === 'number'
          ? `${props.size}px`
          : props.size
      ),
    )

    return () => {
      const cmpClass = [
        'd-icon',
        props.color && `--color-${props.color}`,
        '--color-static',
        presetSize.value && `--size-icon-${props.size}`,
        props.spin && 'd-spin',
        state.pressing && 'pressing',
      ]

      const cmpStyle = {
        transform: `rotate(${props.rotate}deg)`,
        ...customSize.value && {
          width: customSize.value,
          height: customSize.value,
          lineHeight: customSize.value,
        },
      }

      const bindings = {
        onClick: (e: MouseEvent) => emit('click', e),
        onMousedown: (e: MouseEvent) => {
          emit('mousedown', e)
          state.pressing = true
        },
        onMouseenter: (e: MouseEvent) => {
          emit('mouseenter', e)
          state.hover = true
        },
        onMouseleave: (e: MouseEvent) => {
          emit('mouseleave', e)
          state.hover = false
          state.pressing = false
        },
        onMouseup: (e: MouseEvent) => {
          emit('mouseup', e)
          state.pressing = false
        },
        class: cmpClass,
        style: cmpStyle,
      }

      return (
        // eslint-disable-next-line no-nested-ternary
        props.icon
          ? <span
            v-html={
              props.icon(
                {
                  size: '100%',
                  theme: props.theme,
                  strokeWidth: props.strokeWidth,
                  strokeLinecap: props.strokeLinecap,
                  strokeLinejoin: props.strokeLinejoin,
                  fill: props.fill,
                },
              )
            }
            {...bindings}
          />
          : <span {...bindings}>
            {slots.default?.()}
          </span>
      )
    }
  },
})
